import type { LucideIcon } from 'lucide-react';
import {
  ArrowUpRight,
  BarChart3,
  CheckCircle,
  Code,
  Globe,
  Megaphone,
  Palette,
  Search,
  ShieldCheck,
  ShoppingCart,
  Smartphone,
  Users,
} from 'lucide-react';

import PixelBackground from '@/components/PixelBackground';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import FAQSection from '@/components/FAQSection';
import Seo from '@/components/Seo';
import Display from '@/components/typography/Display';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Container, HeroSection, Section } from '@/components/ui/Section';
import { useLanguage } from '@/contexts/LanguageContext';
import { useLocation } from 'react-router-dom';
import { BASE_URL, getHreflangLinks, getPageMeta } from '@/seo/meta';
import { stripLanguagePrefix, useLocalizedPath, withLanguagePrefix } from '@/lib/locale';

type Locale = 'en' | 'el';

type ServiceSuiteBase = {
  key: 'product' | 'ecommerce' | 'branding' | 'marketing' | 'seo' | 'apps' | 'content';
  href: string;
  icon: LucideIcon;
};

type ServiceSuiteContent = {
  title: string;
  description: string;
  highlights: string[];
};

type DifferentiatorBase = {
  key: 'squads' | 'outcomes' | 'ops' | 'global';
  icon: LucideIcon;
};

type DifferentiatorContent = {
  title: string;
  description: string;
};

type HeroContent = {
  eyebrow: string;
  leading: string;
  accent: string;
  accentLines?: string[];
  description: string;
  partnershipEyebrow: string;
  partnershipTitle: string;
  partnershipDescription: string;
  partnershipCalloutTitle: string;
  partnershipCalloutDescription: string;
  primaryCta: string;
  secondaryCta: string;
};

type KickoffStep = {
  title: string;
  description: string;
};

type KickoffContent = {
  eyebrow: string;
  heading: string;
  steps: KickoffStep[];
  dontHeading: string;
  donts: string[];
};

type FaqItem = {
  question: string;
  answer: string;
};

type FaqContent = {
  title: string;
  subtitle: string;
  items: FaqItem[];
  footer: {
    prefix: string;
    linkLabel: string;
    suffix?: string;
  };
};

type FinalCtaContent = {
  eyebrow: string;
  title: string;
  button: string;
};

type Metric = {
  value: string;
  label: string;
};

const serviceSuitesBase: ServiceSuiteBase[] = [
  { key: 'product', href: '/services/web-design', icon: Code },
  { key: 'ecommerce', href: '/services/ecommerce', icon: ShoppingCart },
  { key: 'apps', href: '/services/apps', icon: Smartphone },
  { key: 'marketing', href: '/services/marketing-strategy', icon: Megaphone },
  { key: 'seo', href: '/services/seo', icon: Search },
  { key: 'branding', href: '/services/branding', icon: Palette },
] as const;

const differentiatorsBase: DifferentiatorBase[] = [
  { key: 'squads', icon: Users },
  { key: 'outcomes', icon: BarChart3 },
  { key: 'ops', icon: ShieldCheck },
  { key: 'global', icon: Globe },
] as const;

const heroCopy: Record<Locale, HeroContent> = {
  en: {
    eyebrow: 'Services',
    leading: 'Digital solutions design & development',
    accent: 'services',
    accentLines: undefined,
    description:
      'Websites, e-shops, and apps (web & mobile), with an emphasis on performance, functionality, and your business growth.',
    partnershipEyebrow: 'How we collaborate',
    partnershipTitle: 'We work with you, not just for you.',
    partnershipDescription:
      'We become an extension of your team and move forward with pace and transparency.',
    partnershipCalloutTitle: '',
    partnershipCalloutDescription: '',
    primaryCta: 'Book a free consultation',
    secondaryCta: 'See our portfolio',
  },
  el: {
    eyebrow: 'Υπηρεσίες',
    leading: 'Υπηρεσίες κατασκευής & ανάπτυξης',
    accent: 'ψηφιακών λύσεων',
    accentLines: undefined,
    description:
      'Κατασκευή ιστοσελίδων, e-shop και εφαρμογών (web & mobile), με έμφαση στην απόδοση, τη λειτουργικότητα και την ανάπτυξη της επιχείρησής σας.',
    partnershipEyebrow: 'Τρόπος συνεργασίας',
    partnershipTitle: 'Δουλεύουμε μαζί, όχι απλώς για εσάς.',
    partnershipDescription:
      'Γινόμαστε προέκταση της ομάδας σας και προχωράμε με ρυθμό και διαφάνεια.',
    partnershipCalloutTitle: '',
    partnershipCalloutDescription: '',
    primaryCta: 'Κλείστε δωρεάν συμβουλευτική',
    secondaryCta: 'Δείτε το portfolio μας',
  },
};

const heroStatsCopy: Record<Locale, Metric[]> = {
  en: [
    { value: 'From 3–6 weeks', label: 'Average time for initial delivery' },
    { value: 'Clear scope from the start', label: 'No “extras” or ambiguity along the way' },
    { value: 'Ongoing partnerships', label: 'Most clients return for the next phase' },
  ],
  el: [
    { value: 'Από 2–6 εβδομάδες', label: 'Μέσος χρόνος παράδοσης' },
    { value: 'Έτοιμη για χρήση', label: 'Πλήρως λειτουργική στο λανσάρισμα' },
    { value: 'Συνεργασίες με συνέχεια', label: 'Οι περισσότεροι πελάτες επιστρέφουν' },
  ],
};

const partnershipCopy: Record<Locale, string[]> = {
  en: [
    'Clear steps and goals from the start',
    'Continuous communication and real progress',
    'We measure results, not just deliverables',
  ],
  el: [
    'Ξεκάθαρα βήματα και στόχοι από την αρχή',
    'Συνεχής επικοινωνία και πραγματική πρόοδος',
    'Μετράμε αποτελέσματα, όχι απλώς παραδόσεις',
  ],
};

const serviceCopy: Record<Locale, Record<ServiceSuiteBase['key'], ServiceSuiteContent>> = {
  en: {
    product: {
      title: 'Websites',
      description:
        'We design websites that deliver results, functionality, and a professional image.',
      highlights: [
        'Clear project plan from the start',
        'Modern design and performance',
        'Post-launch support',
      ],
    },
    ecommerce: {
      title: 'E-commerce',
      description:
        'We design functional stores that deliver results.',
      highlights: [
        'Clear project plan from the start',
        'Modern design and performance',
        'Post-launch support',
      ],
    },
    branding: {
      title: 'Branding & Image',
      description:
        'We design identities that deliver results, consistency, and a professional image.',
      highlights: [
        'Clear project plan from the start',
        'Modern design and performance',
        'Post-launch support',
      ],
    },
    marketing: {
      title: 'Advertising',
      description:
        'We design campaigns that deliver results, traffic, and a professional image.',
      highlights: [
        'Clear project plan from the start',
        'Modern design and performance',
        'Post-launch support',
      ],
    },
    seo: {
      title: 'SEO',
      description:
        'Technical and content optimization that improves visibility and qualified traffic.',
      highlights: [
        'Full SEO audit and priorities',
        'On-page and technical fixes',
        'Ongoing reporting and improvements',
      ],
    },
    apps: {
      title: 'Web & Mobile Apps',
      description:
        'We design apps that deliver results, stability, and a professional image.',
      highlights: [
        'Clear project plan from the start',
        'Modern design and performance',
        'Post-launch support',
      ],
    },
    content: {
      title: 'Content',
      description:
        'We design content that delivers results, clarity, and a professional image.',
      highlights: [
        'Clear project plan from the start',
        'Modern design and performance',
        'Post-launch support',
      ],
    },
  },
  el: {
    product: {
      title: 'Ιστοσελίδες',
      description:
        'Σχεδιάζουμε λύσεις που φέρνουν αποτέλεσμα, λειτουργικότητα και επαγγελματική εικόνα.',
      highlights: [
        'Ξεκάθαρο πλάνο υλοποίησης έργου',
        'Σύγχρονος σχεδιασμός και απόδοση',
        'Υποστήριξη μετά το λανσάρισμα',
      ],
    },
    ecommerce: {
      title: 'E-commerce',
      description:
        'Σχεδιάζουμε λειτουργικά καταστήματα που φέρνουν αποτέλεσμα',
      highlights: [
        'Ξεκάθαρο πλάνο υλοποίησης έργου',
        'Σύγχρονος σχεδιασμός και απόδοση',
        'Υποστήριξη μετά το λανσάρισμα',
      ],
    },
    branding: {
      title: 'Branding & Εικόνα',
      description:
        'Σχεδιάζουμε ταυτότητες που φέρνουν αποτέλεσμα, συνέπεια και επαγγελματική εικόνα.',
      highlights: [
        'Ξεκάθαρο πλάνο υλοποίησης έργου',
        'Σύγχρονος σχεδιασμός και απόδοση',
        'Υποστήριξη μετά το λανσάρισμα',
      ],
    },
    marketing: {
      title: 'Διαφήμιση',
      description:
        'Σχεδιάζουμε καμπάνιες που φέρνουν αποτέλεσμα, επισκεψιμότητα και επαγγελματική εικόνα.',
      highlights: [
        'Ξεκάθαρο πλάνο υλοποίησης έργου',
        'Σύγχρονος σχεδιασμός και απόδοση',
        'Υποστήριξη μετά το λανσάρισμα',
      ],
    },
    seo: {
      title: 'SEO',
      description:
        'Τεχνική βελτιστοποίηση περιεχομένου για ορατότητα και ποιοτική επισκεψιμότητα.',
      highlights: [
        'Πλήρες SEO audit και προτεραιότητες',
        'On-page και τεχνικές διορθώσεις',
        'Συνεχής αναφορά και βελτιώσεις',
      ],
    },
    apps: {
      title: 'Web & Mobile Apps',
      description:
        'Σχεδιάζουμε εφαρμογές που φέρνουν αποτέλεσμα, σταθερότητα και επαγγελματική εικόνα.',
      highlights: [
        'Ξεκάθαρο πλάνο υλοποίησης έργου',
        'Σύγχρονος σχεδιασμός και απόδοση',
        'Υποστήριξη μετά το λανσάρισμα',
      ],
    },
    content: {
      title: 'Περιεχόμενο',
      description:
        'Σχεδιάζουμε περιεχόμενο που φέρνει αποτέλεσμα, κατανόηση και επαγγελματική εικόνα.',
      highlights: [
        'Ξεκάθαρο πλάνο υλοποίησης έργου',
        'Σύγχρονος σχεδιασμός και απόδοση',
        'Υποστήριξη μετά το λανσάρισμα',
      ],
    },
  },
};

const differentiatorCopy: Record<Locale, Record<DifferentiatorBase['key'], DifferentiatorContent>> = {
  en: {
    squads: {
      title: 'Starting digitally now',
      description: '→ Website or E-shop',
    },
    outcomes: {
      title: 'Want more customers',
      description: '→ Advertising',
    },
    ops: {
      title: 'Have a product but no brand image',
      description: '→ Branding & Content',
    },
    global: {
      title: 'Need something more complex',
      description: '→ Web & Mobile Apps',
    },
  },
  el: {
    squads: {
      title: 'Ξεκινάτε τώρα ψηφιακά',
      description: '→ Ιστοσελίδα ή E-shop',
    },
    outcomes: {
      title: 'Θέλετε περισσότερους πελάτες',
      description: '→ Διαφήμιση',
    },
    ops: {
      title: 'Έχετε προϊόν αλλά όχι εικόνα',
      description: '→ Branding & Περιεχόμενο',
    },
    global: {
      title: 'Χρειάζεστε κάτι πιο σύνθετο',
      description: '→ Web & Mobile Apps',
    },
  },
};

const capabilitiesCopy: Record<Locale, { eyebrow: string; heading: string; description: string }> = {
  en: {
    eyebrow: 'Service Packages',
    heading: 'Everything you need to get started and grow online.',
    description:
      'We take you from the first idea to launch and promotion, with solutions that work in practice.',
  },
  el: {
    eyebrow: 'Πακέτα Υπηρεσιών',
    heading: 'Ό,τι χρειάζεστε για να ξεκινήσετε και να αναπτυχθείτε online.',
    description:
      'Αναλαμβάνουμε από την πρώτη ιδέα μέχρι τη λειτουργία και την προώθηση, με λύσεις που δουλεύουν στην πράξη.',
  },
};

const differentiatorsSectionCopy: Record<Locale, { eyebrow: string; heading: string; description: string }> = {
  en: {
    eyebrow: 'Service selection guide',
    heading: 'What do you actually need?',
    description:
      "If you're not sure where to start, here are the most common paths.",
  },
  el: {
    eyebrow: 'Οδηγός επιλογής υπηρεσίας',
    heading: 'Τι χρειάζεστε πραγματικά;',
    description:
      'Αν δεν είστε σίγουροι από πού να ξεκινήσετε, δείτε τις πιο συνηθισμένες περιπτώσεις.',
  },
};

const kickoffCopy: Record<Locale, KickoffContent> = {
  en: {
    eyebrow: 'How we start',
    heading: 'Simple steps, no commitments.',
    steps: [
      {
        title: 'Free conversation',
        description: 'We talk about your goal and what you have today.',
      },
      {
        title: 'Proposal with clear steps',
        description: 'We propose a solution, timeline, and cost.',
      },
      {
        title: 'We start step by step',
        description: 'We deliver by priorities with room to expand.',
      },
    ],
    dontHeading: "What we don't do",
    donts: [
      "We don't sell services you don't need",
      "We don't start without a clear goal",
      "We don't leave you after delivery",
    ],
  },
  el: {
    eyebrow: 'Πώς ξεκινάμε',
    heading: 'Απλά βήματα, χωρίς δεσμεύσεις',
    steps: [
      {
        title: 'Δωρεάν συζήτηση',
        description: 'Μιλάμε για τον στόχο σας και τι έχετε σήμερα.',
      },
      {
        title: 'Πρόταση με ξεκάθαρα βήματα',
        description: 'Σας προτείνουμε λύση, χρονοδιάγραμμα και κόστος.',
      },
      {
        title: 'Ξεκινάμε σταδιακά',
        description: 'Υλοποιούμε με προτεραιότητες και δυνατότητα επέκτασης.',
      },
    ],
    dontHeading: 'Τι δεν κάνουμε',
    donts: [
      'Δεν πουλάμε υπηρεσίες που δεν χρειάζεστε',
      'Δεν ξεκινάμε χωρίς ξεκάθαρο στόχο',
      'Δεν σας αφήνουμε μετά την παράδοση',
    ],
  },
};

const faqCopy: Record<Locale, FaqContent> = {
  en: {
    title: 'Frequently asked questions before you start',
    subtitle: 'Answers to the most common questions.',
    items: [
      {
        question: 'How much does it cost?',
        answer:
          'It depends on what you need. Before we start, we give you a clear proposal with scope and price.',
      },
      {
        question: 'Can I start small?',
        answer: 'Yes. Many collaborations start with a first step and grow over time.',
      },
      {
        question: 'How long does it take?',
        answer: 'Usually 3–6 weeks for the initial delivery, depending on the project.',
      },
      {
        question: 'What happens after launch?',
        answer: 'We stay with you for support, improvements, or next steps if needed.',
      },
      {
        question: 'Do I need technical knowledge?',
        answer: 'No. We handle the process and explain everything in simple terms.',
      },
    ],
    footer: {
      prefix: "Can't find what you're looking for? See all ",
      linkLabel: 'FAQs',
      suffix: '.',
    },
  },
  el: {
    title: 'Συχνές ερωτήσεις πριν ξεκινήσετε',
    subtitle: 'Απαντήσεις στις πιο συχνές ερωτήσεις.',
    items: [
      {
        question: 'Πόσο κοστίζει;',
        answer:
          'Το κόστος εξαρτάται από το τι χρειάζεστε. Πριν ξεκινήσουμε, σας δίνουμε ξεκάθαρη πρόταση με scope και τιμή.',
      },
      {
        question: 'Μπορώ να ξεκινήσω με κάτι μικρό;',
        answer: 'Ναι. Πολλές συνεργασίες ξεκινούν με ένα πρώτο βήμα και εξελίσσονται στην πορεία.',
      },
      {
        question: 'Πόσο χρόνο χρειάζεται;',
        answer: 'Συνήθως 3–6 εβδομάδες για την αρχική υλοποίηση, ανάλογα με το έργο.',
      },
      {
        question: 'Τι γίνεται μετά το λανσάρισμα;',
        answer:
          'Παραμένουμε δίπλα σας για υποστήριξη, βελτιώσεις ή επόμενα βήματα, αν το χρειαστείτε.',
      },
      {
        question: 'Χρειάζεται να έχω τεχνικές γνώσεις;',
        answer: 'Όχι. Αναλαμβάνουμε εμείς τη διαδικασία και σας εξηγούμε τα πάντα με απλά λόγια.',
      },
    ],
    footer: {
      prefix: 'Δεν βρήκατε αυτό που ψάχνετε; Δείτε όλες τις ',
      linkLabel: 'συχνές ερωτήσεις',
      suffix: '.',
    },
  },
};

const finalCtaCopy: Record<Locale, FinalCtaContent> = {
  en: {
    eyebrow: 'Not sure?',
    title: 'Talk with us.',
    button: 'Book a free consultation',
  },
  el: {
    eyebrow: 'Δεν είστε σίγουροι;',
    title: 'Μιλήστε μαζί μας.',
    button: 'Κλείστε δωρεάν συμβουλευτική',
  },
};

export default function ServicesIndex() {
  const { language } = useLanguage();
  const location = useLocation();
  const locale: Locale = language === 'el' ? 'el' : 'en';
  const localizedPath = useLocalizedPath();
  const meta = getPageMeta('services', undefined, locale);
  const canonicalPath = withLanguagePrefix(stripLanguagePrefix(location.pathname), locale);
  const canonicalUrl = `${BASE_URL}${canonicalPath}`;
  const hreflang = getHreflangLinks(location.pathname, locale);

  const hero = heroCopy[locale];
  const heroStats = heroStatsCopy[locale];
  const partnershipPoints = partnershipCopy[locale];

  const serviceSuites = serviceSuitesBase.map(({ key, href, icon }) => ({
    href,
    icon,
    ...serviceCopy[locale][key],
  }));

  const differentiators = differentiatorsBase.map(({ key, icon }) => ({
    icon,
    ...differentiatorCopy[locale][key],
  }));

  const capabilitiesSection = capabilitiesCopy[locale];
  const differentiatorsSection = differentiatorsSectionCopy[locale];
  const kickoff = kickoffCopy[locale];
  const faq = faqCopy[locale];
  const finalCta = finalCtaCopy[locale];
  const faqHref = localizedPath('/resources/faq');

  return (
    <div className="relative min-h-screen bg-black text-white">
      <Seo
        title={meta.title}
        description={meta.description}
        url={canonicalUrl}
        hreflang={hreflang}
        image={meta.image}
      />
      <PixelBackground />

      <div className="relative z-10 flex min-h-screen flex-col">
        <Header variant="simple" />

        <main className="flex-1">
          <HeroSection className="relative overflow-hidden pt-32 pb-20 md:pt-36 lg:pt-40 lg:pb-24">
            <div className="absolute inset-0 geometric-pattern" />
            <div className="absolute inset-0 bg-gradient-to-br from-pp-black/95 via-black/85 to-black" />

            <Container className="relative">
              <div className="grid grid-cols-1 gap-10 sm:gap-12 lg:gap-14 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,0.8fr)] lg:items-start">
                <div className="space-y-8">
                  <div className="space-y-5">
                    <p className="text-[11px] font-medium uppercase tracking-[0.4em] text-brand-highlight/70">
                      {hero.eyebrow}
                    </p>
                    <Display
                      variant="h1"
                      as="h1"
                      className="text-white text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-[1.12] tracking-tight"
                    >
                      <span className="block text-white">{hero.leading}</span>
                      {hero.accentLines
                        ? hero.accentLines.map((line) => (
                            <span key={line} className="block text-gradient">
                              {line}
                            </span>
                          ))
                        : (
                          <span className="block text-gradient">{hero.accent}</span>
                        )}
                    </Display>
                  </div>

                  <Display
                    variant="subhead"
                    as="p"
                    className="max-w-2xl text-base sm:text-lg leading-relaxed text-white/70"
                  >
                    {hero.description}
                  </Display>

                  <div className="flex flex-col gap-4 sm:flex-row">
                    <Button
                      as="button"
                      onClick={() => window.navigateToConsultation?.()}
                      trackCta={{ text: hero.primaryCta, section: 'services_hero', variant: 'primary' }}
                      variant="primary"
                      size="lg"
                      className="w-full justify-center sm:w-auto"
                    >
                      {hero.primaryCta}
                    </Button>
                    <Button
                      as="button"
                      onClick={() => window.navigateToPortfolio?.()}
                      trackCta={{ text: hero.secondaryCta, section: 'services_hero', variant: 'secondary' }}
                      variant="secondary"
                      size="lg"
                      className="w-full justify-center sm:w-auto"
                    >
                      {hero.secondaryCta}
                    </Button>
                  </div>
                </div>

                <div className="relative mt-4 sm:mt-6 lg:mt-8">
                  <div className="pointer-events-none absolute -top-10 -right-14 h-32 w-32 rounded-full bg-brand-highlight/20 blur-3xl" />
                  <div className="pointer-events-none absolute -bottom-12 -left-16 h-36 w-36 rounded-full bg-white/10 blur-3xl" />
                  <div className="relative w-full rounded-3xl border border-white/15 bg-black/70 p-6 shadow-glass sm:max-w-md sm:p-7 lg:ml-auto lg:p-9">
                    <div className="space-y-6">
                      <div>
                        <p className="text-[11px] uppercase tracking-[0.35em] text-brand-highlight/70">
                          {hero.partnershipEyebrow}
                        </p>
                        <h3 className="mt-3 text-lg font-semibold text-white sm:text-xl md:text-2xl">
                          {hero.partnershipTitle}
                        </h3>
                        <p className="mt-3 text-sm text-white/70">{hero.partnershipDescription}</p>
                      </div>
                      <ul className="space-y-4 text-sm text-white/70">
                        {partnershipPoints.map((point) => (
                          <li key={point} className="flex items-start gap-3">
                            <span className="mt-0.5 flex h-6 w-6 items-center justify-center rounded-full bg-brand-highlight/15">
                              <CheckCircle className="h-4 w-4 text-brand-highlight" />
                            </span>
                            <span>{point}</span>
                          </li>
                        ))}
                      </ul>
                      {(hero.partnershipCalloutTitle || hero.partnershipCalloutDescription) ? (
                        <div className="rounded-2xl border border-white/10 bg-black/60 px-6 py-4 text-sm text-white/70 shadow-inner">
                          <p className="font-medium text-white">{hero.partnershipCalloutTitle}</p>
                          <p className="mt-1">{hero.partnershipCalloutDescription}</p>
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-10 grid gap-6 border-t border-white/10 pt-8 sm:grid-cols-3 sm:gap-0 sm:divide-x sm:divide-white/10">
                {heroStats.map((stat) => (
                  <div key={stat.label} className="space-y-1 py-2 sm:px-6">
                    <p className="text-base font-semibold tracking-tight text-white md:text-base">{stat.value}</p>
                    <p className="text-[11px] uppercase tracking-[0.2em] text-white/55">{stat.label}</p>
                  </div>
                ))}
              </div>
            </Container>
          </HeroSection>

          <Section id="capabilities">
            <Container>
              <div className="max-w-3xl space-y-4">
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-brand-highlight/80">
                  {capabilitiesSection.eyebrow}
                </p>
                <h2 className="text-3xl font-semibold text-white md:text-5xl">
                  {capabilitiesSection.heading}
                </h2>
                <p className="text-lg text-white/70">{capabilitiesSection.description}</p>
              </div>

              <div className="mt-12 grid grid-cols-1 gap-8 md:grid-cols-2 xl:grid-cols-3">
                {serviceSuites.map((service) => {
                  const Icon = service.icon;
                  return (
                    <a
                      key={service.title}
                      href={service.href}
                      className="group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/40 focus-visible:ring-offset-2 focus-visible:ring-offset-black"
                    >
                      <Card className="h-full space-y-6 rounded-2xl border-white/15 bg-black/70 backdrop-blur-xl p-6 transition-all duration-300 hover:bg-white/10 hover:border-white/25 hover:shadow-lg hover:shadow-white/10">
                        <div className="flex items-center justify-between gap-6">
                          <div className="flex h-12 w-12 items-center justify-center rounded-full border border-white/10 bg-white/5 text-white transition-colors duration-300 group-hover:border-brand-highlight/70 group-hover:text-brand-highlight">
                            <Icon className="h-6 w-6" />
                          </div>
                          <ArrowUpRight className="h-5 w-5 text-white/40 transition-colors duration-300 group-hover:text-brand-highlight" />
                        </div>
                        <div className="space-y-3">
                          <h3 className="text-2xl font-semibold text-white">{service.title}</h3>
                          <p className="text-sm text-white/70">{service.description}</p>
                        </div>
                        <ul className="space-y-3 text-sm text-white/75">
                          {service.highlights.map((highlight) => (
                            <li key={highlight} className="flex items-start gap-2">
                              <span className="mt-1 h-1.5 w-1.5 rounded-full bg-white/40 transition-colors duration-300 group-hover:bg-brand-highlight/80" />
                              <span>{highlight}</span>
                            </li>
                          ))}
                        </ul>
                      </Card>
                    </a>
                  );
                })}
              </div>
            </Container>
          </Section>

          <Section className="relative overflow-hidden bg-dark-900/25">
            <div className="absolute inset-0 geometric-pattern opacity-40" />
            <Container className="relative">
              <div className="max-w-3xl space-y-4">
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-brand-highlight/80">
                  {differentiatorsSection.eyebrow}
                </p>
                <h2 className="text-3xl font-semibold text-white md:text-5xl">
                  {differentiatorsSection.heading}
                </h2>
                <p className="text-lg text-white/70">{differentiatorsSection.description}</p>
              </div>

              <div className="mt-12 grid grid-cols-1 gap-8 md:grid-cols-2 xl:grid-cols-4">
                {differentiators.map((item) => {
                  const Icon = item.icon;
                  return (
                    <div
                      key={item.title}
                      className="group glassmorphic glassmorphic-hover h-full rounded-3xl border border-white/10 p-8 transition-transform duration-300 hover:-translate-y-1 hover:border-white/25 hover:shadow-white/10"
                    >
                      <div className="mb-6 flex h-12 w-12 items-center justify-center rounded-full border border-white/10 bg-white/5 text-white transition-colors duration-300 group-hover:border-brand-highlight/70 group-hover:text-brand-highlight">
                        <Icon className="h-6 w-6" />
                      </div>
                      <h3 className="text-xl font-semibold text-white">{item.title}</h3>
                      <p className="mt-3 text-sm text-white/70">{item.description}</p>
                    </div>
                  );
                })}
              </div>

            </Container>
          </Section>

          <Section>
            <Container>
              <div className="max-w-3xl space-y-4">
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-brand-highlight/80">
                  {kickoff.eyebrow}
                </p>
                <h2 className="text-3xl font-semibold text-white md:text-5xl">
                  {kickoff.heading}
                </h2>
              </div>

              <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3">
                {kickoff.steps.map((step, index) => (
                  <Card
                    key={step.title}
                    className="h-full rounded-2xl border-white/15 bg-black/70 backdrop-blur-xl p-6"
                  >
                    <div className="flex items-center gap-4">
                      <span className="flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-white/5 text-sm font-semibold text-white">
                        {(index + 1).toString().padStart(2, '0')}
                      </span>
                      <h3 className="text-lg font-semibold text-white">{step.title}</h3>
                    </div>
                    <p className="text-sm text-white/70">{step.description}</p>
                  </Card>
                ))}
              </div>

              <div className="mt-12 rounded-3xl border border-white/10 bg-black/60 p-8 shadow-glass">
                <h3 className="text-2xl font-semibold text-white">{kickoff.dontHeading}</h3>
                <ul className="mt-6 grid gap-3 text-sm text-white/70 sm:grid-cols-3">
                  {kickoff.donts.map((item) => (
                    <li
                      key={item}
                      className="group flex items-start gap-3 rounded-2xl border border-white/5 bg-black/60 px-5 py-4 transition hover:border-white/15"
                    >
                      <span className="mt-1 h-2 w-2 rounded-full bg-brand-highlight/70 transition-colors duration-300 group-hover:bg-brand-highlight" />
                      <span className="leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Container>
          </Section>

          <FAQSection
            title={faq.title}
            subtitle={faq.subtitle}
            items={faq.items}
            footerLink={{
              ...faq.footer,
              href: faqHref,
            }}
          />

          <Section className="pb-32 lg:pb-48">
            <Container className="max-w-4xl">
              <div className="rounded-[32px] border border-white/15 bg-black/70 p-12 text-center shadow-glass">
                <p className="text-xs font-semibold uppercase tracking-[0.35em] text-brand-highlight/80">
                  {finalCta.eyebrow}
                </p>
                <h2 className="mt-4 text-3xl font-semibold text-white md:text-4xl">{finalCta.title}</h2>
                <div className="mt-8 flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
                  <Button
                    as="button"
                    onClick={() => window.navigateToConsultation?.()}
                    trackCta={{ text: finalCta.button, section: 'services_final_cta', variant: 'primary' }}
                    variant="primary"
                    size="lg"
                    className="w-full justify-center sm:w-auto"
                  >
                    {finalCta.button}
                  </Button>
                </div>
              </div>
            </Container>
          </Section>
        </main>

        <Footer variant="compact" />
      </div>
    </div>
  );
}
